import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AccessDenied, errorStatus } from '@/components/RouteError';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AttachmentList } from '@/components/finance/AttachmentList';
import { SettleDialog } from '@/components/finance/SettleDialog';
import { apartmentsQuery } from '@/lib/apartments';
import { errorMessage } from '@/lib/errors';
import {
  financeAccountsQuery,
  financeCategoriesQuery,
  financeExpectedQuery,
  financeSettingsQuery,
  todayInput,
} from '@/lib/finance';
import { usePermissions } from '@/lib/me';
import { formatMinor } from '@/lib/money';
import { FINANCE_EXPECTED_STATUS_TONE } from '@/lib/status-tone';
import type { FinanceExpected, FinanceQueueItem } from '@/api/generated/types.gen';

export const Route = createFileRoute('/_app/finance/expected/$id')({
  component: ExpectedDetailRoute,
});

const CANCEL_REASON_LABEL: Record<NonNullable<FinanceExpected['cancel_reason']>, string> = {
  rule_paused: 'the rule was paused',
  rule_changed: 'the rule changed',
  booking_cancelled: 'the booking was cancelled',
  booking_moved: 'the booking moved',
  split: 'split into parts',
  operator: 'cancelled by an operator',
};

function toQueueItem(e: FinanceExpected, todayStr: string): FinanceQueueItem {
  return {
    source: 'expected',
    target: { expected_id: e.id },
    direction: e.direction,
    amount_minor: e.amount_minor,
    currency: e.currency,
    settled_minor: e.settled_minor,
    due_date: e.due_date,
    period_month: e.period_month,
    overdue: e.status === 'planned' && e.due_date < todayStr,
    today: e.due_date === todayStr,
    category_id: e.category_id,
    apartment_id: e.apartment_id,
    booking_id: e.booking_id,
    counterparty_id: e.counterparty_id,
    origin: e.origin,
    note: e.note,
    expected: e,
  };
}

// The route reads the param; the page takes it as a prop (same split as the
// movement/rule/account detail pages). `key={id}` forces a full remount on a
// param change — SettleDialog below is mounted unconditionally on this page
// (toggled by `confirming`, not by the id), and without the remount its
// useOperationId(item?.target.expected_id) would still key correctly off
// the NEW id after a param change (the hook already reacts to a changed
// formKey — lib/finance.ts) but `confirming` itself is plain component
// state with no such protection: it would still read `true` from row A's
// session after a client-side navigation straight to row B's URL, popping
// the dialog open on a page the operator has not asked to confirm yet.
function ExpectedDetailRoute() {
  const { id } = Route.useParams();
  return <ExpectedDetailPage key={id} id={id} />;
}

export function ExpectedDetailPage({ id }: { id: string }) {
  const { can } = usePermissions();
  const [confirming, setConfirming] = useState(false);

  const expected = useQuery(financeExpectedQuery(id));
  const { data: accounts = [] } = useQuery(financeAccountsQuery());
  const { data: categories = [] } = useQuery(financeCategoriesQuery());
  const { data: apartments = [] } = useQuery(apartmentsQuery);
  const { data: settings } = useQuery(financeSettingsQuery);

  if (!can('finance.read') || errorStatus(expected.error) === 403) return <AccessDenied />;
  if (expected.isLoading) return <p className="text-sm text-muted">Loading…</p>;
  if (expected.isError || !expected.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {errorMessage(expected.error)}
      </p>
    );
  }

  const e = expected.data;
  const tone = FINANCE_EXPECTED_STATUS_TONE[e.status];
  const categoryName = categories.find((c) => c.id === e.category_id)?.name ?? e.category_id;
  const apartmentName = e.apartment_id ? (apartments.find((a) => a.id === e.apartment_id)?.title ?? e.apartment_id) : undefined;
  const hasHistory = !!(e.moved_from_due_date || e.split_of || e.cancel_reason);
  const canConfirm = can('finance.confirm') && e.status === 'planned';
  const todayStr = todayInput(settings?.business_tz);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-[var(--text-heading)]">{categoryName}</h1>
          <p className="text-sm text-muted">
            {formatMinor(e.amount_minor, e.currency)} · due {e.due_date}
            {apartmentName ? ` · ${apartmentName}` : ''}
          </p>
        </div>
        <Badge variant={tone.variant} dot>
          {tone.label}
        </Badge>
      </div>

      {e.booking_id && (
        <Link to="/bookings/$id" params={{ id: e.booking_id }} className="text-sm text-primary hover:underline">
          View booking →
        </Link>
      )}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-4 text-sm">
        <dt className="text-muted">Settled so far</dt>
        <dd className="tabular-nums">{formatMinor(e.settled_minor, e.currency)}</dd>
        <dt className="text-muted">Period</dt>
        <dd>{e.period_month}</dd>
        <dt className="text-muted">Origin</dt>
        <dd className="capitalize">{e.origin}</dd>
        {e.note && (
          <>
            <dt className="text-muted">Note</dt>
            <dd>{e.note}</dd>
          </>
        )}
      </dl>

      {hasHistory && (
        <section className="flex flex-col gap-1">
          <h2 className="text-sm font-semibold uppercase tracking-[0.06em] text-muted">History</h2>
          <ul className="flex flex-col gap-0.5 text-sm text-foreground">
            {e.moved_from_due_date && <li>Moved from {e.moved_from_due_date} to {e.due_date}.</li>}
            {e.split_of && (
              <li>
                Split from{' '}
                <Link to="/finance/expected/$id" params={{ id: e.split_of }} className="text-primary hover:underline">
                  another expected payment
                </Link>
                .
              </li>
            )}
            {e.cancel_reason && <li>Cancelled — {CANCEL_REASON_LABEL[e.cancel_reason]}.</li>}
          </ul>
        </section>
      )}

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-[0.06em] text-muted">Receipts</h2>
        {e.attachments && e.attachments.length > 0 ? (
          <AttachmentList items={e.attachments} />
        ) : (
          <p className="text-sm text-muted">No receipts attached.</p>
        )}
      </section>

      {canConfirm && (
        <div>
          <Button variant="primary" onClick={() => setConfirming(true)}>
            Confirm
          </Button>
        </div>
      )}

      <SettleDialog
        item={confirming ? toQueueItem(e, todayStr) : null}
        accounts={accounts}
        categories={categories}
        businessTz={settings?.business_tz}
        onClose={() => setConfirming(false)}
      />
    </div>
  );
}
